/**
 * 方案 C 一键操作行(42px 常驻,侧栏展开态 footer)。
 * 像素/结构真相源:handoff variants.jsx VariantC(56-137 行),类名 nvc- 前缀。
 * 纯展示组件:浮层 pop 是唯一局部 state,所有动作经 props 回调上抛。
 * 行尾 ⋯ 按钮不受 busy(打卡动作 pending)禁用,是只读自助入口。
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BriefcaseBusiness, Coffee, MoreHorizontal, Play, Square } from "lucide-react";
import "./clock-panel.css";
import {
  fmtClip,
  fmtHMS,
  formatHm,
  type BreakTypeId,
  type ClockDisplayMode,
} from "./derive";
import type { ClockActionKind } from "./useClockActions";
import type { ClockTick } from "./useClockTicker";
import type { WorkTypeOption } from "./useWorkTypeOptions";
import { BreakPicker, ConfirmClockOut, WorkTypePicker } from "./pickers";
import { ClockMoreMenu, type MoreActionsConfig } from "./ClockMoreMenu";

export interface ClockActionRowProps {
  displayMode: ClockDisplayMode;
  tick: ClockTick;
  /** runningSpecial?.title。 */
  activityName: string | null;
  actionsAllowed: boolean;
  pending: ClockActionKind | null;
  workTypeOptions: WorkTypeOption[];
  workTypeLoading: boolean;
  onClockIn: () => void;
  onClockOut: () => void;
  onStartBreak: (t: BreakTypeId) => void;
  onEndBreak: () => void;
  onStartWorkType: (code: string, label: string, notes: string) => void;
  onEndWorkType: () => void;
  /** ⋯ more actions 菜单配置;null = 不渲染入口(登录态未就绪)。 */
  moreActions: MoreActionsConfig | null;
}

type PopKind = "break" | "work" | "confirm" | "more" | null;

export function ClockActionRow(props: ClockActionRowProps) {
  const { t } = useTranslation("shifts");
  const {
    displayMode,
    tick,
    activityName,
    actionsAllowed,
    pending,
    workTypeOptions,
    workTypeLoading,
    onClockIn,
    onClockOut,
    onStartBreak,
    onEndBreak,
    onStartWorkType,
    onEndWorkType,
    moreActions,
  } = props;

  const [pop, setPop] = useState<PopKind>(null);
  const busy = pending != null;

  // 状态变化(动作生效/他端打卡)→ 浮层复位(VariantC 60 行)
  useEffect(() => {
    setPop(null);
  }, [displayMode]);

  // Esc 关闭(仅浮层开着时挂监听,core.jsx useEsc 同语义)
  useEffect(() => {
    if (!pop) return undefined;
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPop(null);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [pop]);

  const toggle = (k: Exclude<PopKind, null>) => setPop((p) => (p === k ? null : k));

  // ── row 形态(VariantC 65-107 行逐行对应) ───────────────
  let inner: React.ReactNode;
  if (displayMode === "loading") {
    // 首拉未完成:只读 loading 行,绝不渲染可点打卡按钮(防时序误点)
    inner = (
      <>
        <span className="nvc-dot gray nvc-loading" />
        <span className="nvc-stlabel gray" style={{ flex: 1 }}>
          {t("attendanceClock.loading")}
        </span>
      </>
    );
  } else if (displayMode === "idle" || displayMode === "ended") {
    inner = actionsAllowed ? (
      <button
        className="nvc-btn green"
        style={{ flex: 1 }}
        onClick={onClockIn}
        disabled={busy}
      >
        <Play size={10} fill="currentColor" />
        {t("attendanceClock.clockIn")}
        <span className="bt">
          {displayMode === "ended" ? formatHm(tick.workSec) : "00:00:00"}
        </span>
      </button>
    ) : (
      // 只读:灰点 + 标签(+ ended 静止计时),无按钮;label flex:1 把右侧内容推到行尾
      <>
        <span className="nvc-dot gray" />
        <span className="nvc-stlabel gray" style={{ flex: 1 }}>
          {displayMode === "ended" ? t("attendanceClock.shiftEnded") : t("attendanceClock.notClockedIn")}
        </span>
        {displayMode === "ended" && (
          <span className="nvc-timer gray">{formatHm(tick.workSec)}</span>
        )}
      </>
    );
  } else if (displayMode === "break") {
    inner = (
      <>
        <span className="nvc-dot amber" />
        <span className="nvc-stlabel amber">{t("attendanceClock.break")}</span>
        <span className="nvc-timer amber">{fmtHMS(tick.curBreakSec)}</span>
        {actionsAllowed && (
          <button className="nvc-cbtn-sm green" onClick={onEndBreak} disabled={busy}>
            <Play size={9} fill="currentColor" />
            End
          </button>
        )}
      </>
    );
  } else if (displayMode === "activity") {
    inner = (
      <>
        <span className="nvc-dot green" />
        <span className="nvc-timer green" style={{ marginLeft: 2 }}>
          {fmtHMS(tick.workSec)}
        </span>
        <span className="nvc-cpill">
          <BriefcaseBusiness size={11} />
          <span className="nm">{activityName}</span>
          <span className="tm">{fmtClip(tick.actSec)}</span>
          {actionsAllowed && (
            <button
              className="stop"
              title={t("attendanceClock.endActivity")}
              onClick={onEndWorkType}
              disabled={busy}
            >
              <Square size={8} fill="currentColor" />
            </button>
          )}
        </span>
      </>
    );
  } else {
    // working
    inner = (
      <>
        <span className="nvc-dot green" />
        <span className="nvc-timer green" style={{ marginLeft: 2, marginRight: "auto" }}>
          {fmtHMS(tick.workSec)}
        </span>
        {actionsAllowed && (
          <>
            <button
              className="nvc-iconbtn"
              title={t("attendanceClock.break")}
              onClick={() => toggle("break")}
              disabled={busy}
            >
              <Coffee size={14} />
            </button>
            <button
              className="nvc-iconbtn"
              title={t("attendanceClock.workType")}
              onClick={() => toggle("work")}
              disabled={busy}
            >
              <BriefcaseBusiness size={14} />
            </button>
            <button
              className="nvc-iconbtn danger"
              title={t("attendanceClock.clockOut")}
              onClick={() => toggle("confirm")}
              disabled={busy}
            >
              <Square size={11} fill="currentColor" />
            </button>
          </>
        )}
      </>
    );
  }

  // ⋯ 按钮:只读自助入口,不受 busy(打卡动作 pending)禁用
  const moreBtn = moreActions ? (
    <button
      className="nvc-iconbtn"
      title={t("attendanceClock.moreActions")}
      onClick={() => toggle("more")}
    >
      <MoreHorizontal size={14} />
    </button>
  ) : null;

  return (
    <div className="nvc-clock nvc-rel" style={{ padding: "4px 6px" }}>
      <div className="nvc-crow">
        {inner}
        {moreBtn}
      </div>
      {pop === "break" && (
        <div className="nvc-mini">
          <BreakPicker
            disabled={busy}
            onBack={() => setPop(null)}
            onPick={(t) => {
              onStartBreak(t);
              setPop(null);
            }}
          />
        </div>
      )}
      {pop === "work" && (
        <div className="nvc-mini">
          <WorkTypePicker
            options={workTypeOptions}
            loading={workTypeLoading}
            disabled={busy}
            onBack={() => setPop(null)}
            onStart={(code, label, notes) => {
              onStartWorkType(code, label, notes);
              setPop(null);
            }}
          />
        </div>
      )}
      {pop === "confirm" && (
        <div className="nvc-mini" style={{ width: 200 }}>
          <ConfirmClockOut
            disabled={busy}
            onCancel={() => setPop(null)}
            onConfirm={() => {
              onClockOut();
              setPop(null);
            }}
          />
        </div>
      )}
      {pop === "more" && moreActions && (
        <div className="nvc-mini" style={{ width: 190 }}>
          <ClockMoreMenu actions={moreActions} onClose={() => setPop(null)} />
        </div>
      )}
    </div>
  );
}
